import { IUser } from '../user';

/**
 * Purchase analytics reported by the game client.
 *
 * These records say what the client observed, not what was granted. A row is evidence
 * for support and reconciliation, never proof of entitlement -- LootLocker remains the
 * authority on what a player actually owns.
 */

export enum ETransactionKind {
  /** Real money to Credits, via LootLocker's Steam purchase redemption. */
  CurrencyPurchase = 'currency_purchase',
  /** Credits or Manashards to an asset, via a LootLocker catalog purchase. */
  ItemPurchase = 'item_purchase',
}

export enum ETransactionStatus {
  /** Reported before the outcome is known. Stuck here is the state worth chasing. */
  Initiated = 'initiated',
  Succeeded = 'succeeded',
  Failed = 'failed',
  Cancelled = 'cancelled',
}

export enum ETransactionVerification {
  Pending = 'pending',
  Verified = 'verified',
  /** LootLocker and the client's report disagree. */
  Discrepancy = 'discrepancy',
  Closed = 'closed',
}

export enum ETransactionCurrency {
  Usd = 'usd',
  Credits = 'cds',
  Manashards = 'ms',
}

export interface ITransactionStage {
  stage: string;
  status: string;
  detail?: string;
  at: string;
}

export interface ITransaction {
  _id: string;

  kind: ETransactionKind;
  status: ETransactionStatus;

  /** Populated on the admin listing, an id elsewhere. */
  user: IUser | string;

  lootlockerUlid?: string;
  steamId?: string;
  idempotencyKey: string;

  currency: ETransactionCurrency;
  amount: number;

  entitlementId?: string;
  steamPurchaseStatus?: string;
  creditsGranted?: number;
  walletCreditedAt?: string;

  catalogListingId?: string;
  itemDefId?: number;
  itemName?: string;
  balanceBefore?: number;

  statusCode?: number;
  errorCode?: string;
  errorMessage?: string;
  /** LootLocker's own ids -- what their support needs to trace one transaction. */
  requestId?: string;
  traceId?: string;

  stages: ITransactionStage[];

  verification: ETransactionVerification;
  verifiedAt?: string;
  verificationNote?: string;

  gameVersion?: string;

  createdAt: string;
  updatedAt: string;
}

/** Counts and sums grouped by flow, outcome and currency. */
export interface ITransactionSummaryRow {
  kind: ETransactionKind;
  status: ETransactionStatus;
  currency: ETransactionCurrency;
  count: number;
  amount: number;
  creditsGranted: number;
}

export const getTransactionKindName = (kind: ETransactionKind): string => {
  switch (kind) {
    case ETransactionKind.CurrencyPurchase:
      return 'Currency';

    case ETransactionKind.ItemPurchase:
      return 'Item';

    default:
      return kind;
  }
};

export const getTransactionCurrencyName = (currency: ETransactionCurrency): string => {
  switch (currency) {
    case ETransactionCurrency.Usd:
      return 'USD';

    case ETransactionCurrency.Credits:
      return 'Credits';

    case ETransactionCurrency.Manashards:
      return 'Manashards';

    default:
      return currency;
  }
};

export const getTransactionVerificationName = (verification: ETransactionVerification): string => {
  switch (verification) {
    case ETransactionVerification.Pending:
      return 'Not checked';

    case ETransactionVerification.Verified:
      return 'Verified';

    case ETransactionVerification.Discrepancy:
      return 'Discrepancy';

    case ETransactionVerification.Closed:
      return 'Closed';

    default:
      return verification;
  }
};

/**
 * Background and foreground for a badge.
 *
 * Both halves are needed. This project runs Bootstrap 4 CSS under react-bootstrap 2, which
 * emits Bootstrap 5's `bg-*` utilities -- and in Bootstrap 4 those set a background without
 * setting a text colour. On a pale background the label then inherits the dark theme's white
 * and disappears, which is what made the `pending` pill unreadable.
 */
export interface ITransactionBadgeVariant {
  bg: string;
  text?: 'dark';
}

export const getTransactionStatusVariant = (status: ETransactionStatus): ITransactionBadgeVariant => {
  switch (status) {
    case ETransactionStatus.Succeeded:
      return { bg: 'success' };

    case ETransactionStatus.Failed:
      return { bg: 'danger' };

    case ETransactionStatus.Cancelled:
      return { bg: 'secondary' };

    case ETransactionStatus.Initiated:
      return { bg: 'warning', text: 'dark' };

    default:
      return { bg: 'secondary' };
  }
};

export const getTransactionVerificationVariant = (verification: ETransactionVerification): ITransactionBadgeVariant => {
  switch (verification) {
    case ETransactionVerification.Verified:
      return { bg: 'success' };

    case ETransactionVerification.Discrepancy:
      return { bg: 'danger' };

    case ETransactionVerification.Closed:
      return { bg: 'secondary' };

    case ETransactionVerification.Pending:
      return { bg: 'light', text: 'dark' };

    default:
      return { bg: 'secondary' };
  }
};
